import { TemplateRef } from "@angular/core";
import { Observable } from "rxjs";

// Column Types
export type ColumnType =
  | "text"
  | "number"
  | "currency"
  | "date"
  | "datetime"
  | "checkbox"
  | "badge"
  | "template"
  | "actions"
  | "index";

// Column Alignment
export type ColumnAlign = "left" | "center" | "right";

// Action Types
export interface TableAction {
  type: string;
  icon?: string;
  title?: string;
  tooltip?: string;
  class?: string;
  display?: boolean | ((row: any) => boolean);
  permission?: string;
}

// Badge Configuration
export interface BadgeConfig {
  field: string; // Field để lấy giá trị hiển thị
  colorField?: string; // Field để lấy class màu
  colorMap?: { [key: string]: string }; // Map value -> class
  colorFn?: (row: any) => string; // Function để lấy class
}

// Column Configuration
export interface ColumnConfig {
  fieldName: string;
  title: string;
  type?: ColumnType;
  width?: string;
  minWidth?: string;
  align?: ColumnAlign;
  alignBody?: ColumnAlign;

  // Formatting
  format?: string; // Date format: 'dd/MM/yyyy HH:mm'
  pipe?: "number" | "currency" | "date" | "percent";

  // Sorting
  sortable?: boolean;
  isSort?: boolean; // Backward compatibility alias for sortable
  sortDirection?: 1 | -1 | null;
  sortField?: string; // Field khác để sort

  // Display options
  show?: boolean;
  isShow?: boolean; // Backward compatibility alias for show
  showIf?: () => boolean;
  class?: string;
  headerClass?: string;

  isTreeColumn?: boolean;

  // Custom display function
  display?: (row: any) => any;

  // Badge config
  badge?: BadgeConfig;

  // Template
  template?: TemplateRef<any>;

  // Callback
  callback?: (row: any) => void;

  // For summary row
  summary?: "sum" | "count" | "avg" | "custom";
  summaryFn?: (data: any[]) => any;

  // Permission
  permission?: string;
}

// Display Configuration
export interface DisplayConfig {
  enableNumbering?: boolean;
  enableSelect?: boolean;
  enableSearch?: boolean;
  enablePagination?: boolean;
  showSummaryRow?: boolean;
  showNoData?: boolean;
  noDataText?: string;
  stripedRows?: boolean;
  hoverRows?: boolean;
  bordered?: boolean;
  condensed?: boolean;
  // Fixed columns
  fixedActionsColumn?: boolean; // Fixed cột hành động bên phải
  fixedCheckboxColumn?: boolean; // Fixed cột checkbox bên trái
  fixedIndexColumn?: boolean; // Fixed cột số thứ tự bên trái
  // Tree Config
  treeMode?: boolean;
  treeChildrenField?: string;
}

// Pagination Configuration
export interface PaginationConfig {
  pageSize?: number;
  pageSizeOptions?: number[];
  showSizeChanger?: boolean;
  showTotal?: boolean;
  maxSize?: number;
  boundaryLinks?: boolean;
  showGoToPage?: boolean; // Option để hiện/ẩn Go to page
}

// Table Configuration
export class TableConfig {
  id?: string; // Unique ID for storage
  columns: ColumnConfig[] = [];
  actions?: TableAction[] = [];
  display?: DisplayConfig = {
    enableNumbering: true,
    enableSelect: false,
    enablePagination: true,
    showSummaryRow: false,
    showNoData: true,
    noDataText: "Không có dữ liệu",
    stripedRows: false,
    hoverRows: true,
    bordered: true,
    condensed: true,
  };
  pagination?: PaginationConfig = {
    pageSize: 10,
    pageSizeOptions: [10, 20, 30, 40, 50, 100],
    showSizeChanger: true,
    showTotal: true,
    maxSize: 3,
    boundaryLinks: true,
  };
  actionsWidth?: string = "130px";
  actionsTitle?: string = "Hành động";
  rowClass?: string | ((row: any) => string);
  trackByFn?: (index: number, item: any) => any;
}

// Helper class for creating table configs
export class TableConfigBuilder {
  private config: TableConfig = new TableConfig();

  columns(columns: ColumnConfig[]): TableConfigBuilder {
    this.config.columns = columns;
    return this;
  }

  actions(actions: TableAction[]): TableConfigBuilder {
    this.config.actions = actions;
    return this;
  }

  display(display: DisplayConfig): TableConfigBuilder {
    this.config.display = { ...this.config.display, ...display };
    return this;
  }

  pagination(pagination: PaginationConfig): TableConfigBuilder {
    this.config.pagination = { ...this.config.pagination, ...pagination };
    return this;
  }

  actionsWidth(width: string): TableConfigBuilder {
    this.config.actionsWidth = width;
    return this;
  }

  build(): TableConfig {
    return this.config;
  }
}

// Column builder helper
export class ColumnBuilder {
  private column: ColumnConfig;

  constructor(fieldName: string, title: string) {
    this.column = { fieldName, title, type: "text", show: true };
  }

  static text(fieldName: string, title: string): ColumnBuilder {
    return new ColumnBuilder(fieldName, title).type("text");
  }

  static number(fieldName: string, title: string): ColumnBuilder {
    return new ColumnBuilder(fieldName, title).type("number").align("right");
  }

  static currency(fieldName: string, title: string): ColumnBuilder {
    return new ColumnBuilder(fieldName, title).type("currency").align("right");
  }

  static date(
    fieldName: string,
    title: string,
    format = "dd/MM/yyyy"
  ): ColumnBuilder {
    return new ColumnBuilder(fieldName, title).type("date").format(format);
  }

  static datetime(
    fieldName: string,
    title: string,
    format = "dd/MM/yyyy HH:mm"
  ): ColumnBuilder {
    return new ColumnBuilder(fieldName, title).type("datetime").format(format);
  }

  static badge(
    fieldName: string,
    title: string,
    colorMap?: { [key: string]: string }
  ): ColumnBuilder {
    const builder = new ColumnBuilder(fieldName, title)
      .type("badge")
      .align("center");
    if (colorMap) {
      builder.column.badge = { field: fieldName, colorMap };
    }
    return builder;
  }

  static template(fieldName: string, title: string): ColumnBuilder {
    return new ColumnBuilder(fieldName, title).type("template");
  }

  static index(title = "#"): ColumnBuilder {
    return new ColumnBuilder("_index", title)
      .type("index")
      .align("center")
      .width("50px");
  }

  type(type: ColumnType): ColumnBuilder {
    this.column.type = type;
    return this;
  }

  width(width: string): ColumnBuilder {
    this.column.width = width;
    return this;
  }

  minWidth(minWidth: string): ColumnBuilder {
    this.column.minWidth = minWidth;
    return this;
  }

  align(align: ColumnAlign): ColumnBuilder {
    this.column.align = align;
    this.column.alignBody = align;
    return this;
  }

  alignBody(align: ColumnAlign): ColumnBuilder {
    this.column.alignBody = align;
    return this;
  }

  format(format: string): ColumnBuilder {
    this.column.format = format;
    return this;
  }

  sortable(sortable = true): ColumnBuilder {
    this.column.sortable = sortable;
    return this;
  }

  showIf(fn: () => boolean): ColumnBuilder {
    this.column.showIf = fn;
    return this;
  }

  class(className: string): ColumnBuilder {
    this.column.class = className;
    return this;
  }

  summary(
    type: "sum" | "count" | "avg" | "custom",
    fn?: (data: any[]) => any
  ): ColumnBuilder {
    this.column.summary = type;
    if (fn) this.column.summaryFn = fn;
    return this;
  }

  permission(permission: string): ColumnBuilder {
    this.column.permission = permission;
    return this;
  }

  onClick(callback: (row: any) => void): ColumnBuilder {
    this.column.callback = callback;
    return this;
  }

  build(): ColumnConfig {
    return this.column;
  }
}
